// Pure check-in logic (plan Rev. 3 items 27-34), no React, unit-tested from server/__tests__.
// Input is GET /api/onboarding/status (3a-2 step 8); every step may be { state: "unavailable" }.

import { isHotelEdition } from "@shared/edition";

type Unavailable = { state: "unavailable" };

export type CheckinStatus = {
  hotel?: { state: "done" | "open" } | Unavailable;
  channels?: { state: "done" | "open"; instagram: boolean; facebook: boolean; linkedin: boolean } | Unavailable;
  strategy?:
    | { state: "none" | "running" | "failed"; pillars: string[] }
    | { state: "ready"; pillars: string[]; refinement: "preliminary" | "refined"; refining: boolean }
    | Unavailable;
  photos?: { state: "done" | "open"; total: number; byPillar: Record<string, number> } | Unavailable;
  house?: { state: "done" | "open" } | Unavailable;
  firstWeek?: { state: "setting_up" | "waiting" | "done" } | Unavailable;
};

export const CHECKIN_STEPS = [
  { n: 1, key: "hotel", title: "Ihr Hotel" },
  { n: 2, key: "channels", title: "Kanäle verbinden" },
  { n: 3, key: "strategy", title: "Strategie und Themen" },
  { n: 4, key: "photos", title: "Fotos zu Ihren Themen" },
  { n: 5, key: "house", title: "Ihr Haus", optional: true },
  { n: 6, key: "firstWeek", title: "Erste Woche freigeben" },
] as const;

export type StepKey = (typeof CHECKIN_STEPS)[number]["key"];

/** Photo goal (item 31): 5 per theme, or 20 overall; without a strategy one tile with goal 20. */
export const PHOTOS_PER_PILLAR = 5;
export const PHOTOS_TOTAL = 20;

export function photosDone(pillars: string[], byPillar: Record<string, number>, total: number): boolean {
  if (total >= PHOTOS_TOTAL) return true;
  if (pillars.length === 0) return false;
  return pillars.every((p) => (byPillar[p] ?? 0) >= PHOTOS_PER_PILLAR);
}

/** Done flags per step. "unavailable" never counts as done. */
export function stepDone(status: CheckinStatus | undefined): Record<StepKey, boolean> {
  const s = status ?? {};
  const pillars = s.strategy && "pillars" in s.strategy ? s.strategy.pillars : [];
  const photos = s.photos && s.photos.state !== "unavailable" ? s.photos : null;
  return {
    hotel: s.hotel?.state === "done",
    channels: s.channels?.state === "done",
    strategy: s.strategy?.state === "ready",
    photos: !!photos && photosDone(pillars, photos.byPillar, photos.total),
    house: s.house?.state === "done",
    firstWeek: s.firstWeek?.state === "done",
  };
}

/** Counter "x/6" (item 27): step 5 counts once saved; "finished" needs the five required steps. */
export function checkinProgress(status: CheckinStatus | undefined): { done: number; total: 6; finished: boolean } {
  const d = stepDone(status);
  const done = CHECKIN_STEPS.filter((s) => d[s.key]).length;
  const finished = CHECKIN_STEPS.filter((s) => !("optional" in s && s.optional)).every((s) => d[s.key]);
  return { done, total: 6, finished };
}

/** First step that still needs the hotel, for the landing step; all done → step 6. */
export function firstOpenStep(status: CheckinStatus | undefined): number {
  const d = stepDone(status);
  const open = CHECKIN_STEPS.find((s) => !d[s.key] && !("optional" in s && s.optional));
  return open ? open.n : 6;
}

/** Session flag set by "Zum Dashboard": the cockpit stays the start page for this browser session. */
export const CHECKIN_SKIP_KEY = "checkinSkipped";
/** Set before the OAuth round trip to /platform-connections (query params get lost there, item 29). */
export const CHECKIN_RETURN_KEY = "checkinReturn";

/** The phone QR in the photos step is the general upload link (/upload/<token>); its photos go to the n8n
 *  photo workflow. Check-in photos of a hotel are only stored, so hotels do not see the QR (Mike 2026-10-10).
 *  Company not loaded yet → hidden, so a hotel never sees it flash up. */
export function showCheckinUploadQr(
  company: { dfyProfile?: string | null; edition?: { kind?: string } | null } | undefined,
  uploadToken: string | null | undefined,
): boolean {
  if (!uploadToken || !company) return false;
  return company.edition?.kind !== "hotel" && !isHotelEdition(company);
}
